import React, { useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Button } from '@mui/material';
import { useNotification } from '../../contexts/NotificationContext';
import { usePatientForm } from './shared/hooks/usePatientForm';
import { usePatient } from './shared/services/usePatientService';
import PatientForm from './shared/PatientForm';
import FormPageLayout from '../../components/FormPageLayout';
import DraftSaveButton from '../../components/DraftSaveButton';
import { DRAFT_KEYS } from '../../utils/draftUtils';
import { Box } from '@mui/material';

const PatientPage = () => {
  const { uhid_no } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { showNotification } = useNotification();

  const { isEdit, loading, existingData, nextUhid, dataLoaded, getDraft, clearSavedData, submit } = usePatient(uhid_no);
  const { formData, errors, setInitialData, handleChange, handlePhoneChange, handleEmailChange, handleAllergyChange, handleAttendantRelationChange, handleOnlinePaymentChange, handleCardTypeChange, handleBlur, validate, buildPayload } = usePatientForm();

  useEffect(() => { if (existingData) setInitialData(existingData); }, [existingData]);

  useEffect(() => {
    if (!isEdit && dataLoaded && nextUhid) {
      setInitialData(prev => {
        const updated = { ...prev, uhidNo: nextUhid };
        const draft = getDraft(nextUhid);
        return draft?.formData ? { ...updated, ...draft.formData } : updated;
      });
    }
  }, [isEdit, dataLoaded, nextUhid]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate(showNotification)) return;
    if (formData.anyAllergies && formData.mentionAllergies.length === 0) {
      showNotification('error', "Please mention at least one allergy or uncheck 'Any Allergies'.");
      return;
    }
    try {
      await submit(buildPayload(formData.mentionAllergies));
      showNotification('success', isEdit ? 'Patient details updated successfully!' : 'Patient registration submitted successfully!');
      if (!isEdit) clearSavedData(formData.uhidNo);
      
      const returnPage = location.state?.page || 0;
      const returnMenu = location.state?.activeMenu || 'Patients';
      
      setTimeout(() => {
        navigate(`/home?page=${returnPage}`, { 
          state: { activeMenu: returnMenu } 
        });
      }, 1000);
    } catch (error) {
      showNotification('error', error.response?.data?.error || 'Error saving patient data!');
    }
  };

  return (
    <FormPageLayout 
      title="PATIENT REGISTRATION FORM" 
      backTo={{ 
        path: `/home?page=${location.state?.page || 0}`, 
        state: { activeMenu: location.state?.activeMenu || 'Patients' } 
      }} 
      loading={loading}
    >
      <form onSubmit={handleSubmit}>
        <PatientForm formData={formData} errors={errors} handleChange={handleChange} handleBlur={handleBlur} handlePhoneChange={handlePhoneChange} handleEmailChange={handleEmailChange} handleAllergyChange={handleAllergyChange} handleAttendantRelationChange={handleAttendantRelationChange} handleOnlinePaymentChange={handleOnlinePaymentChange} handleCardTypeChange={handleCardTypeChange} />
        <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
          {!isEdit && <DraftSaveButton formKey={DRAFT_KEYS.ADD_PATIENT} uhidNo={formData.uhidNo || 'default'} formData={formData} onDraftLoaded={(draft) => { if (draft?.formData) setInitialData(draft.formData); }} variant="outlined" />}
          <Button type="submit" variant="contained" color="primary" fullWidth>{isEdit ? 'Update Patient Registration' : 'Submit Registration'}</Button>
        </Box>
      </form>
    </FormPageLayout>
  );
};

export default PatientPage;
